import { effect, inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { ChatStore } from '../store';
import { ProjectRefService } from '../project-ref.service';
import { environment } from 'src/environments/environment';

/**
 * Syncs `projecttoken` and `thread_id` cookies for authenticated asset requests.
 *
 * **Why cookies?** Asset images (portraits, thumbnails) within vis.js graph nodes and raw `<img src>` tags
 * cannot carry custom headers. Browsers attach cookies automatically to all same-origin requests,
 * including `<img>` tags and XMLHttpRequest/fetch inside vis-network. This service ensures these
 * security-critical cookies stay in sync with the chat state, allowing the backend to authenticate
 * asset downloads by reading the cookies directly.
 */
@Injectable({ providedIn: 'root' })
export class AiCookieService {
	private readonly cookieService = inject(CookieService);
	private readonly projectRef = inject(ProjectRefService);
	private readonly chatStore = inject(ChatStore);
	private readonly options = { path: '/', sameSite: 'Lax' as const , ...(environment.ENV_NAME !== 'DEV' ? { domain: `.${environment.DOMAIN}` } : {})};

	constructor() {
		console.log('AiCookieService: constructor');
		// effect() must live in the constructor to guarantee a valid injection context.
		// Calling effect() outside the constructor throws NG0203 error because Angular
		// injection context is only available during DI phase.
		effect(() => {
			console.info('AiCookieService: thread_id changed', this.chatStore.thread_id());
			const thread_id = this.chatStore.thread_id();
			if (thread_id) {
				this.cookieService.set('thread_id', thread_id, this.options);
			} else {
				this.cookieService.delete('thread_id', this.options.path);
			}
		});
	}

	/**
	 * Sync the projecttoken cookie from the current project.
	 * ProjectRefService is not yet reactive — call this once when the host component initializes.
	 * If this method is not called, the `projecttoken` cookie will be absent or stale,
	 * causing the backend to reject asset requests (HTTP 401/403).
	 * TODO: Remove this manual call once ProjectRefService becomes a reactive signal.
	 */
	syncProjectToken(): void {
		const projecttoken = this.projectRef.getCurrentProjectToken();
		if (projecttoken) {
			this.cookieService.set('projecttoken', projecttoken, this.options);
		}else{
			this.cookieService.delete('projecttoken', '/');
		}
	}
}
